import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { LEVEL_LABELS, MatchedModule } from '../../types/pathway';
import { matchBand } from '../../utils/pathwayMatching';
interface ModuleCardProps {
  match: MatchedModule;
  inPathway: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onTogglePathway: () => void;
}
const bandStyles: Record<string, string> = {
  Strong: 'border-[#05634B] text-[#007558]',
  Good: 'border-[#5290A6] text-[#30718D]',
  Partial: 'border-[#828282] text-[#6B7280]'
};
export function ModuleCard({
  match,
  inPathway,
  expanded,
  onToggleExpand,
  onTogglePathway
}: ModuleCardProps) {
  const {
    module,
    score,
    matchedTerms
  } = match;
  const band = score !== null ? matchBand(score) : null;
  const detailsId = `module-details-${module.id}`;
  return <article className={`bg-white rounded-xl shadow-sm border transition-colors ${inPathway ? 'border-[#30718D]' : 'border-gray-100'}`} data-id="element-1620" data-mp="3jhc77njdd9un">
      <div className="p-5" data-id="element-1621" data-mp="38divkcpjxdcv">
        <div className="flex items-start gap-4" data-id="element-1622" data-mp="38gwv7d6tz8gm">
          <button type="button" onClick={onTogglePathway} aria-pressed={inPathway} aria-label={inPathway ? `Remove ${module.name} from pathway` : `Add ${module.name} to pathway`} className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-[#30718D] ${inPathway ? 'border-[#30718D] bg-[#30718D] text-white hover:bg-[#265a70]' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-1623" data-mp="33fhc3qo7rc0v">
            {inPathway ? <Check size={16} data-id="element-1624" data-mp="dxqr89gxqvql" /> : <Plus size={16} data-id="element-1625" data-mp="dy9ymw8ym1qd" />}
          </button>

          <div className="min-w-0 flex-1" data-id="element-1626" data-mp="8zyoeker5p7m">
            <h3 className="text-base font-bold text-gray-900" data-id="element-1627" data-mp="908es93plydu">
              {module.name}
            </h3>

            <p className="mt-1.5 text-sm text-gray-600" data-id="element-1628" data-mp="23em8nvy4gve3">{module.description}</p>

            <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-1629" data-mp="287fcmhlrnzp4">
              {band && <span className={`inline-flex h-6 items-center justify-center rounded border bg-white px-3 text-xs font-bold whitespace-nowrap ${bandStyles[band]}`} data-id="element-1630" data-mp="1tj2epf34c6rz">
                  {band} match
                </span>}
              <span className="text-xs text-gray-500" data-id="element-1631" data-mp="2r61gg48o44wq">
                {module.levels.map(l => LEVEL_LABELS[l]).join(', ')}
              </span>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-1.5" data-id="element-1632" data-mp="2cabta2rqxlqf">
              {matchedTerms.length > 0 && <span className="text-xs text-gray-500" data-id="element-1633" data-mp="10t2aligfyxq6">Matched:</span>}
              {matchedTerms.slice(0, 4).map(term => <span key={term} className="inline-flex rounded bg-[#30718D]/10 px-2 py-1 text-xs font-medium text-[#30718D]" data-id="element-1634" data-mp="15sydfedekj45">
                  {term}
                </span>)}
              <span className="inline-flex rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700" data-id="element-1635" data-mp="3f37kn8p5f30t">
                {module.category}
              </span>
            </div>
          </div>

          <button type="button" onClick={onToggleExpand} aria-expanded={expanded} aria-controls={detailsId} className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1636" data-mp="3ox6ivwm3q2nh">
            {expanded ? 'Less' : 'Details'}
            <ChevronDown size={14} className={`transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`} data-id="element-1637" data-mp="2hkxnhn628dsi" />
          </button>
        </div>
      </div>

      <AnimatePresence initial={false} data-id="element-1638" data-mp="2qaec908i81dl">
        {expanded && <motion.div id={detailsId} initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden border-t border-gray-100" data-id="element-1639" data-mp="2v7mxj9oy8lj2">
            <div className="grid gap-6 p-5 sm:grid-cols-2" data-id="element-1640" data-mp="39wj1ovnvxeee">
              <div data-id="element-1641" data-mp="3dy4ll0yu6sy3">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-900" data-id="element-1642" data-mp="3dxmflcmdh0cz">
                  Topics
                </div>
                <ul className="mt-2 space-y-1" data-id="element-1643" data-mp="3ivnz1ynpn1oz">
                  {module.topics.map(topic => <li key={topic} className="text-sm text-gray-700" data-id="element-1644" data-mp="3nur43j3ox7wy">
                      {topic}
                    </li>)}
                </ul>
              </div>
              <div data-id="element-1645" data-mp="1tj2nzdlq2zaz">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-900" data-id="element-1646" data-mp="1tkni5244kg8b">
                  Expected outcomes
                </div>
                <ul className="mt-2 space-y-1" data-id="element-1647" data-mp="1h7jrsd7eegnv">
                  {module.outcomes.map(outcome => <li key={outcome} className="text-sm text-gray-700" data-id="element-1648" data-mp="1ca9vcdlld4qj">
                      {outcome}
                    </li>)}
                </ul>
              </div>
              <div className="sm:col-span-2 border-t border-gray-100 pt-4" data-id="element-1649" data-mp="24x4cp1n43nzs">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-900" data-id="element-1650" data-mp="1zyjcv3brv6bv">
                  Why this came up
                </div>
                <p className="mt-1.5 text-sm text-gray-700" data-id="element-1651" data-mp="1uy52hemp7kao">
                  {module.relevance}
                </p>
                <p className="mt-2 text-xs text-gray-400" data-id="element-1652" data-mp="1q2zm0vc2extz">
                  Skill Module ID: {module.courseId}
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </article>;
}